import { useContext, useEffect, useMemo } from 'react'

import { fetchExperienceSearchList } from 'actions/ExperienceActions'
import GeoContext from 'contexts/geoContext'
import useExperienceSearchList from 'hooks/Experiences/useExperienceSearchList'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import { usePlaceByCoords, useLocalizedPlaces } from 'hooks/usePlace'
import { getExperienceListOfferCount, getExperienceListKey, getExperiencePlaceShortName, getExperienceSearchPath } from 'lib/experiences/experienceUtils'
import { getDiscoverNearDestinations, isDiscoverNearCuratedAnchor } from 'lib/experiences/experiencesHomepageUtils'

export interface ExperiencesHomepageDiscoverNearData {
  placeName: string
  searchUrl: string
  totalOfferCount: number
  destinations: Array<{
    key: string
    title: string
    offerCount?: number
    image: { id: string, title: string }
    to: string
  }>
  fetching: boolean
}

const DISCOVER_NEAR_PLACE_LEVELS: Array<App.PlaceType> = ['multi_city_vicinity', 'city']

function useExperiencesHomepageDiscoverNearData(): ExperiencesHomepageDiscoverNearData | null {
  const dispatch = useAppDispatch()
  const geo = useContext(GeoContext)
  const experienceLists = useAppSelector((state) => state.experience.experienceLists)
  const [anchorPlace] = usePlaceByCoords(
    geo.latitude,
    geo.longitude,
    DISCOVER_NEAR_PLACE_LEVELS,
  )
  const curatedAnchorPlace = anchorPlace && isDiscoverNearCuratedAnchor(anchorPlace.id) ? anchorPlace : undefined

  const anchorSearchList = useExperienceSearchList({
    placeId: curatedAnchorPlace?.id,
    sortBy: 'type',
    enabled: !!curatedAnchorPlace?.id,
    countOnly: true,
  })

  const rawDestinations = useMemo(() => {
    if (!curatedAnchorPlace) {
      return []
    }

    return getDiscoverNearDestinations(curatedAnchorPlace.id)
  }, [curatedAnchorPlace])

  const destinationListPlaceIds = useMemo(() => {
    return Array.from(new Set(rawDestinations.map((destination) => destination.placeId)))
  }, [rawDestinations])
  const localizedDestinationPlaces = useLocalizedPlaces(destinationListPlaceIds)

  useEffect(() => {
    destinationListPlaceIds.forEach((placeId) => {
      dispatch(fetchExperienceSearchList(placeId))
    })
  }, [destinationListPlaceIds, dispatch])

  const destinationListsFetching = useMemo(() => {
    if (destinationListPlaceIds.length === 0) {
      return false
    }

    return destinationListPlaceIds.some((placeId) => {
      const destinationListKey = getExperienceListKey({ placeId })
      const destinationListState = experienceLists[destinationListKey]
      return !destinationListState || destinationListState.fetching
    })
  }, [destinationListPlaceIds, experienceLists])

  return useMemo(() => {
    if (!curatedAnchorPlace) {
      return null
    }

    const placeName = getExperiencePlaceShortName(curatedAnchorPlace)
    const searchUrl = getExperienceSearchPath(curatedAnchorPlace)

    const destinations = rawDestinations.flatMap((destination) => {
      const resolvedName = localizedDestinationPlaces[destination.placeId]?.place?.name
      if (!resolvedName) {
        return []
      }

      const place: App.Place = {
        id: destination.placeId,
        name: resolvedName,
        type: destination.type as App.Place['type'],
        countryCode: destination.countryCode,
      }

      const destinationListKey = getExperienceListKey({ placeId: destination.placeId })
      const destinationListState = experienceLists[destinationListKey]
      const offerCount = destinationListState?.error
        ? undefined
        : destinationListState
          ? getExperienceListOfferCount(destinationListState)
          : undefined

      return [{
        key: destination.placeId,
        title: resolvedName,
        offerCount,
        image: { id: destination.imageId, title: resolvedName },
        to: getExperienceSearchPath(place),
      }]
    })

    const totalOfferCount = anchorSearchList.fetching ? 0 : getExperienceListOfferCount(anchorSearchList)
    const fetching = destinationListsFetching || Boolean(anchorSearchList.fetching)

    return {
      placeName,
      searchUrl,
      totalOfferCount,
      destinations,
      fetching,
    }
  }, [
    curatedAnchorPlace,
    anchorSearchList,
    destinationListsFetching,
    experienceLists,
    rawDestinations,
    localizedDestinationPlaces,
  ])
}

export default useExperiencesHomepageDiscoverNearData
